import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApi } from '../api/context';
import { useCreate, useResourceList } from '../api/hooks';
import { ApiError } from '../api/client';
import { Button, IconButton } from '../design/primitives/Button';
import { Dialog } from '../design/primitives/Overlay';
import { Field, Input, Select, Textarea } from '../design/primitives/Input';
import { Banner, EmptyState } from '../design/primitives/Misc';
import { useToast } from '../design/primitives/Toast';
import { Icon } from '../design/icons';
import { canCaptureScreen, captureScreen, CaptureCancelled, imageFromFiles, imageFromPaste } from './screenCapture';
import { snapshot } from './diagnostics';

// The "report an issue" dialog. It answers the three questions a triager always has to ask:
// WHERE were you (the area dropdown, pre-selected from your route but always changeable, plus
// the exact path recorded either way), WHAT did you see (a screenshot captured here, pasted,
// or picked from disk), and WHAT was the browser doing (diagnostics, collected silently).
//
// Screenshots are ordinary attachments on the created report, so this component uploads them
// through the same client.upload() the AttachmentsPanel uses.

export interface FeedbackArea {
  id: string;
  key: string;
  label: string;
  route_prefix: string | null;
  sort_order: number;
  is_active: boolean;
}

const KINDS: { value: string; label: string }[] = [
  { value: 'bug', label: "Something's broken" },
  { value: 'ux', label: 'Confusing or hard to use' },
  { value: 'data', label: 'The numbers look wrong' },
  { value: 'performance', label: 'Too slow' },
  { value: 'idea', label: 'I have an idea' },
  { value: 'improvement', label: 'This could be better' },
  { value: 'question', label: 'I have a question' },
  { value: 'other', label: 'Something else' },
];

const SEVERITIES: { value: string; label: string }[] = [
  { value: 'low', label: 'Minor — I can work around it' },
  { value: 'medium', label: 'Annoying — it slows me down' },
  { value: 'high', label: "Serious — I can't finish my work" },
  { value: 'critical', label: 'Critical — this is blocking everyone' },
];

/** The area whose route_prefix is the longest match for `path`. */
export function areaForPath(areas: FeedbackArea[], path: string): FeedbackArea | null {
  let best: FeedbackArea | null = null;
  for (const a of areas) {
    const prefix = a.route_prefix;
    if (!prefix || !a.is_active) continue;
    if (path === prefix || path.startsWith(prefix.endsWith('/') ? prefix : `${prefix}/`)) {
      if (!best || prefix.length > (best.route_prefix?.length ?? 0)) best = a;
    }
  }
  return best;
}

interface Shot {
  file: File;
  url: string;
  /** True when we took it here rather than the reporter attaching their own. */
  captured: boolean;
}

export function FeedbackDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const api = useApi();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const fileRef = useRef<HTMLInputElement>(null);

  const areasQuery = useResourceList<FeedbackArea>('feedback-areas', { filter: [{ field: 'is_active', op: 'eq', value: true }], sort: 'sort_order', limit: 200 }, { enabled: open });
  const areas = useMemo<FeedbackArea[]>(() => areasQuery.rows ?? [], [areasQuery.rows]);

  const [areaId, setAreaId] = useState('');
  const [touchedArea, setTouchedArea] = useState(false);
  const [kind, setKind] = useState('bug');
  const [severity, setSeverity] = useState('medium');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [steps, setSteps] = useState('');
  const [expected, setExpected] = useState('');
  const [shots, setShots] = useState<Shot[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hidden, setHidden] = useState(false);

  // The route and page title are captured when the dialog OPENS: by the time they hit submit
  // the reporter may have used the capture picker, and we want the screen they were on.
  const [where, setWhere] = useState({ path: '/', title: '' });
  useEffect(() => {
    if (!open) return;
    setWhere({ path: location.pathname + location.search, title: document.title });
  }, [open, location.pathname, location.search]);

  // Pre-select the area from the route — until the reporter picks one themselves.
  useEffect(() => {
    if (!open || touchedArea || areas.length === 0) return;
    const match = areaForPath(areas, location.pathname);
    setAreaId(match?.id ?? '');
  }, [open, touchedArea, areas, location.pathname]);

  const addFiles = useCallback((files: File[]) => {
    if (files.length === 0) return;
    setShots((prev) => [...prev, ...files.map((file) => ({ file, url: URL.createObjectURL(file), captured: false }))]);
  }, []);

  // Print Screen → Ctrl+V is how most people already take a screenshot; make it just work.
  useEffect(() => {
    if (!open) return;
    const onPaste = (e: ClipboardEvent) => {
      const file = imageFromPaste(e);
      if (!file) return;
      e.preventDefault();
      addFiles([file]);
      toast({ title: 'Screenshot pasted' });
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [open, addFiles, toast]);

  // Object URLs are revoked when the dialog closes, never while a thumbnail is still shown.
  const reset = useCallback(() => {
    setShots((prev) => {
      for (const s of prev) URL.revokeObjectURL(s.url);
      return [];
    });
    setTitle('');
    setBody('');
    setSteps('');
    setExpected('');
    setKind('bug');
    setSeverity('medium');
    setTouchedArea(false);
    setError(null);
  }, []);

  const close = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const capture = async () => {
    setError(null);
    try {
      // Step out of our own shot: the dialog is hidden for the picker and the grab.
      const file = await captureScreen({ before: () => setHidden(true), after: () => undefined });
      setShots((prev) => [...prev, { file, url: URL.createObjectURL(file), captured: true }]);
      toast({ title: 'Screenshot captured' });
    } catch (e) {
      if (!(e instanceof CaptureCancelled)) {
        setError(e instanceof Error ? e.message : 'The screenshot could not be taken — you can attach one instead.');
      }
    } finally {
      setHidden(false);
    }
  };

  const create = useCreate<{ id: string; ref: string }, Record<string, unknown>>('feedback');

  const submit = async () => {
    if (!title.trim()) {
      setError('A one-line summary is required.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const report = await create.mutateAsync({
        title: title.trim(),
        body: body.trim() || null,
        steps: steps.trim() || null,
        expected: expected.trim() || null,
        kind,
        severity,
        area_id: areaId || null,
        route_path: where.path,
        page_title: where.title || null,
        context: snapshot(),
      });
      let uploaded = 0;
      for (const shot of shots) {
        try {
          await api.upload({ entity_type: 'feedback', entity_id: report.id }, shot.file);
          uploaded += 1;
        } catch {
          // The report itself is safe; say what did not make it rather than losing the lot.
        }
      }
      const missed = shots.length - uploaded;
      toast({
        title: `Thanks — ${report.ref} is with the team`,
        detail: missed > 0 ? `${missed} screenshot${missed === 1 ? '' : 's'} could not be uploaded; you can add ${missed === 1 ? 'it' : 'them'} from the report.` : undefined,
        action: { label: 'View', run: () => navigate(`/feedback/${report.id}`) },
      });
      close();
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : 'The report could not be sent. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const selected = areas.find((a) => a.id === areaId);

  return (
    <Dialog
      open={open && !hidden}
      onClose={busy ? () => undefined : close}
      size="lg"
      title="Report feedback"
      description="Tell us what happened and where. We record the screen you were on and your browser details automatically."
      locked={busy}
      footer={
        <>
          <span className="hf-feedback-foot">
            {where.path && (
              <>
                <Icon name="map-pin" size={13} aria-hidden /> {selected?.label ?? 'Unfiled'} · <code>{where.path}</code>
              </>
            )}
          </span>
          <Button onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} busy={busy} disabled={busy}>
            Send report
          </Button>
        </>
      }
    >
      <div className="hf-feedback">
        {error && <Banner tone="error">{error}</Banner>}

        <Field label="Where were you?" help="Picked from the screen you were on — change it if the problem is somewhere else.">
          <Select
            value={areaId}
            onChange={(e) => {
              setTouchedArea(true);
              setAreaId(e.target.value);
            }}
            options={[{ value: '', label: areasQuery.isLoading ? 'Loading…' : 'Not sure / not listed' }, ...areas.map((a) => ({ value: a.id, label: a.label }))]}
          />
        </Field>

        <div className="hf-feedback-row">
          <Field label="What kind of feedback is this?">
            <Select value={kind} onChange={(e) => setKind(e.target.value)} options={KINDS} />
          </Field>
          <Field label="How much is it holding you up?">
            <Select value={severity} onChange={(e) => setSeverity(e.target.value)} options={SEVERITIES} />
          </Field>
        </div>

        <Field label="Summary" required help="One line a colleague would recognise.">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="The queue filter resets when I come back from a ticket" data-autofocus />
        </Field>

        <Field label="What happened?">
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="What you did, what you saw instead." />
        </Field>

        <div className="hf-feedback-row">
          <Field label="Steps to reproduce" help="Optional, but it is the fastest route to a fix.">
            <Textarea value={steps} onChange={(e) => setSteps(e.target.value)} rows={3} placeholder={'1. Open the ticket board\n2. Filter by queue\n3. Open a ticket and go back'} />
          </Field>
          <Field label="What did you expect?">
            <Textarea value={expected} onChange={(e) => setExpected(e.target.value)} rows={3} placeholder="The filter should still be applied." />
          </Field>
        </div>

        <div className="hf-feedback-shots">
          <div className="head">
            <span className="lbl">Screenshots</span>
            <span className="hint">Capture this screen, paste one with {navigator.platform?.startsWith('Mac') ? '⌘V' : 'Ctrl+V'}, or attach a file.</span>
          </div>
          <div className="acts">
            {canCaptureScreen() && (
              <Button icon="camera" onClick={capture} disabled={busy}>
                Capture this screen
              </Button>
            )}
            <Button icon="paperclip" onClick={() => fileRef.current?.click()} disabled={busy}>
              Attach an image
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => {
                addFiles(imageFromFiles(e.target.files));
                e.target.value = '';
              }}
            />
          </div>
          <div
            className="drop"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              addFiles(imageFromFiles(e.dataTransfer.files));
            }}
          >
            {shots.length === 0 ? (
              <EmptyState icon="image" title="No screenshots yet" hint="Drop an image here, or use the buttons above." />
            ) : (
              <ul className="hf-feedback-thumbs">
                {shots.map((shot, i) => (
                  <li key={shot.url}>
                    <img src={shot.url} alt={shot.file.name} />
                    <span className="name" title={shot.file.name}>
                      {shot.captured ? 'Captured' : shot.file.name}
                    </span>
                    <IconButton
                      icon="x"
                      label={`Remove ${shot.file.name}`}
                      size="sm"
                      onClick={() =>
                        setShots((prev) => {
                          const next = prev.filter((_, n) => n !== i);
                          URL.revokeObjectURL(shot.url);
                          return next;
                        })
                      }
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}
